<script setup lang="ts">
import AppSidebar from '@/components/AppSidebar.vue';
import SheetProvider from '@/components/providers/SheetProvider.vue';
import ToastProvider from '@/components/providers/ToastProvider.vue';
import TopBar from '@/components/TopBar.vue';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { SidebarProvider } from '@/components/ui/sidebar';
import useApp from '@/composables/useApp.ts';
import type {
    BreadcrumbItem as BreadcrumbItemType,
    SidebarItems,
} from '@/types';

const app = useApp();

const props = withDefaults(
    defineProps<{
        breadcrumbItems?: BreadcrumbItemType[];
        sidebarItems?: SidebarItems;
        containerLayout?: boolean;
    }>(),
    {
        containerLayout: true,
    }
);

const breadcrumbItems = props?.breadcrumbItems ?? app.breadcrumbItems;
const sidebarItems = props?.sidebarItems ?? app.sidebarItems;
</script>

<template>
    <SidebarProvider>
        <AppSidebar :items="sidebarItems" />
        <div class="bg-background divide-border flex-1 divide-y">
            <TopBar />
            <main class="px-6 pt-5 pb-10">
                <Breadcrumb
                    v-if="breadcrumbItems && breadcrumbItems?.length > 0"
                    class="mb-4 pt-0.5"
                >
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink href="/">
                                Dashboard
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <template
                            v-for="(breadcrumb, i) in breadcrumbItems"
                            :key="breadcrumb.url"
                        >
                            <BreadcrumbItem>
                                <BreadcrumbPage
                                    v-if="i === breadcrumbItems.length - 1"
                                    >{{ breadcrumb.title }}</BreadcrumbPage
                                >
                                <BreadcrumbLink
                                    v-else
                                    :href="breadcrumb.url"
                                    >{{ breadcrumb.title }}</BreadcrumbLink
                                >
                            </BreadcrumbItem>
                            <BreadcrumbSeparator
                                v-if="i < breadcrumbItems.length - 1"
                            />
                        </template>
                    </BreadcrumbList>
                </Breadcrumb>

                <div
                    :class="{
                        'max-w-7xl': containerLayout,
                    }"
                >
                    <slot name="default" />
                </div>
            </main>
        </div>
    </SidebarProvider>
    <SheetProvider />
    <ToastProvider />
</template>
